import { useEffect, useRef, useState } from 'react';
import { IntegrationCard } from './integration-card';
import googleAds from '/google-ads.svg';
import hubspot from '/hubspot.png';
import { GoogleAdsIntegrationModal } from './google-ads-integration-modal';
import { MetaAdAccountSelectionModal } from '@/components/glitch-ui/meta-ad-account-selection-modal';
import { HubSpotIntegrationModal } from './hubspot-integration-modal';
import { MetaIntegrationModal } from './meta-integration-modal';
import { useAtomValue } from 'jotai';
import { googleAdsAccountAtom, googleAdsAccountResolvedAtom } from '@/store';
import { useQuery } from '@tanstack/react-query';
import {
  googleAdsAccountsQueryOptions,
  useHubSpotIntegrationStatus,
  useMetaIntegrationStatus,
  userProfileQueryOptions,
} from '@/lib/query-options';
import { useTestMode } from '@/hooks/useTestMode';
import { shouldAutoOpenMetaAccountPicker } from '@/lib/meta';
import { shouldAutoOpenGoogleAccountPicker } from '@/lib/platform-connections';
import { GoogleAdAccountSelectionModal } from '@/components/glitch-ui/google-ad-account-selection-modal';

export const IntegrationSettings = () => {
  // #5936: the integration modal (status / Refresh / Disconnect). Ad-account switching is NOT
  // here — it lives in the user menu ("Ad Accounts → Google"), same split as Meta (#4173).
  const [isOpenGoogleAdsIntegrationModal, setIsOpenGoogleAdsIntegrationModal] = useState<boolean>(false);
  const [isOpenGooglePicker, setIsOpenGooglePicker] = useState<boolean>(false);
  const [isOpenHubSpotModal, setIsOpenHubSpotModal] = useState<boolean>(false);
  const [isOpenMetaModal, setIsOpenMetaModal] = useState<boolean>(false);
  const [isOpenMetaPicker, setIsOpenMetaPicker] = useState<boolean>(false);

  // Slack is a separate coming-soon integration, still test-mode-only (not tied to Meta).
  const isTestMode = useTestMode();
  const selectedGoogleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const { data: hubspotStatus } = useHubSpotIntegrationStatus();
  // #6257: the Meta card (and its status query) is gated behind test mode until Meta App Review
  // clears; the backend flag stays the server-side access control.
  const { data: metaStatus } = useMetaIntegrationStatus({ enabled: isTestMode });

  // Revoked Google token (#5935): /user/me reports the broken flag; the card renders
  // disconnected so the user reconnects (the account modal carries the OAuth refresh CTA).
  const { data: userProfile } = useQuery(userProfileQueryOptions());
  const googleConnectionBroken = userProfile?.google_connection_broken === true;
  // #5936: false only after a voluntary disconnect; undefined (profile still loading)
  // must not flash the card to disconnected.
  const googleAdsDisconnected = userProfile?.google_ads_connected === false;

  const hasSelectedAccount = selectedGoogleAdsAccount !== null && !googleConnectionBroken && !googleAdsDisconnected;

  // Auto-open the Google Ads account picker when the user lands here connected but without a
  // chosen account (#5937) — the Google mirror of the Meta auto-open below. Covers the fresh
  // OAuth return from the integration modal (token stored, accounts synced, nothing picked —
  // without this the card stays "Connect" and the connect flow visibly dead-ends) and stale
  // connections. Ref guards against reopening after a manual dismiss in the same session.
  // On a full reload the atom is null until AppShell hydrates it from the membership, so the
  // check must wait for googleAdsAccountResolvedAtom or it pops on every refresh (#5975).
  const { data: googleAdsAccounts = [] } = useQuery(googleAdsAccountsQueryOptions());
  const googleConnected = userProfile?.google_ads_connected === true && !googleConnectionBroken;
  const googleAccountResolved = useAtomValue(googleAdsAccountResolvedAtom);
  const hasAutoOpenedGooglePicker = useRef(false);
  useEffect(() => {
    if (hasAutoOpenedGooglePicker.current) return;
    if (
      shouldAutoOpenGoogleAccountPicker({
        googleConnected,
        hasAccounts: googleAdsAccounts.length > 0,
        hasSelection: selectedGoogleAdsAccount !== null,
        selectionResolved: googleAccountResolved,
      })
    ) {
      hasAutoOpenedGooglePicker.current = true;
      setIsOpenGooglePicker(true);
    }
  }, [googleConnected, googleAdsAccounts.length, selectedGoogleAdsAccount, googleAccountResolved]);

  // Auto-open the Meta ad-account picker when the user lands here connected but without a chosen
  // ad account — covers two paths: (1) fresh OAuth that just redirected from /meta/callback with
  // multiple ad accounts, and (2) stale connections from before this fix. Ref guards against
  // reopening after a manual dismiss in the same session; a hard refresh re-arms (which is desired
  // since the card is unusable without a selection). #4173 Chunk 11.
  const hasAutoOpenedMetaPicker = useRef(false);
  useEffect(() => {
    if (hasAutoOpenedMetaPicker.current) return;
    if (shouldAutoOpenMetaAccountPicker(metaStatus)) {
      hasAutoOpenedMetaPicker.current = true;
      setIsOpenMetaPicker(true);
    }
  }, [metaStatus]);

  const handleCloseHubSpotModal = () => setIsOpenHubSpotModal(false);
  const handleCloseMetaModal = () => setIsOpenMetaModal(false);
  const handleCloseMetaPicker = () => setIsOpenMetaPicker(false);

  const getGoogleAdsConnectionData = () => {
    // A broken (#5935) or voluntarily-disconnected (#5936) connection renders as
    // disconnected — showing the account as connected while every Google Ads call
    // fails is exactly the silent state those issues remove.
    if (!selectedGoogleAdsAccount || googleConnectionBroken || googleAdsDisconnected) return undefined;

    return {
      connectedDate: new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }),
      accountId: selectedGoogleAdsAccount.googleads_account_id,
      apiKey: selectedGoogleAdsAccount.name,
      serviceName: 'Google Ads',
    };
  };

  const getHubSpotConnectionData = () => {
    if (!hubspotStatus?.is_connected) return undefined;

    return {
      connectedDate: hubspotStatus.created_at
        ? new Date(hubspotStatus.created_at).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
          })
        : 'Unknown',
      accountId: hubspotStatus.account_id || 'Unknown',
      apiKey: hubspotStatus.account_name || 'HubSpot Account',
      serviceName: 'HubSpot',
    };
  };

  const getMetaConnectionData = () => {
    if (!metaStatus?.is_connected) return undefined;

    // The card mirrors Google Ads / HubSpot: shows the *account*, not the connecting human.
    // The auto-opened picker (above) plus the Change-Account-as-picker re-route below guarantee a
    // connected card normally has a selection by the time it renders; if a user dismisses the modal
    // and returns later, the "Not selected" placeholder is the transient state until they pick.
    const selected = metaStatus.selected_ad_account;
    const accountId = selected?.account_id || 'Not selected';
    const accountName = selected
      ? selected.currency
        ? `${selected.name || selected.account_id} · ${selected.currency}`
        : selected.name || selected.account_id
      : '—';

    return {
      connectedDate: metaStatus.created_at
        ? new Date(metaStatus.created_at).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
          })
        : 'Unknown',
      accountId,
      apiKey: accountName,
      serviceName: 'Meta Ads',
    };
  };

  // "Change Account" on the Meta card is dual-purpose: if there's no selected ad account yet (rare,
  // dismiss-modal-and-came-back case), it opens the picker so the user can fix the unusable state;
  // otherwise it opens the HubSpot-parity integration modal (Refresh Connection / Disconnect).
  const handleMetaCardClick = () => {
    if (metaStatus?.is_connected && !metaStatus.selected_ad_account) {
      setIsOpenMetaPicker(true);
    } else {
      setIsOpenMetaModal(true);
    }
  };

  return (
    <div className="space-y-1">
      <div className="grid gap-1 grid-cols-3">
        <IntegrationCard
          title="Google Ads"
          description="Connect your Google Ads account to create, deploy and optimize search campaigns."
          logo={googleAds}
          isEnabled={hasSelectedAccount}
          connectData={getGoogleAdsConnectionData()}
          onToggle={() => setIsOpenGoogleAdsIntegrationModal(true)}
        />

        <IntegrationCard
          title="HubSpot"
          description="Connect your HubSpot CRM to sync deal pipeline conversions with Google Ads and Meta."
          logo={hubspot}
          isEnabled={hubspotStatus?.is_connected || false}
          connectData={getHubSpotConnectionData()}
          onToggle={() => setIsOpenHubSpotModal(true)}
        />

        {/* #6257: Meta UI is gated behind test mode until Meta App Review clears. The backend
            keeps its own flag; this is presentation-only. */}
        {isTestMode && (
          <IntegrationCard
            title="Meta Ads"
            description="Connect your Meta Business account to manage Facebook and Instagram ad campaigns."
            logo="/meta.png"
            isEnabled={metaStatus?.is_connected || false}
            connectData={getMetaConnectionData()}
            onToggle={handleMetaCardClick}
          />
        )}

        {isTestMode && (
          <IntegrationCard
            title="Slack"
            description="Send campaign performance reports and alerts directly to your Slack channels."
            logo="/slack.svg"
            isEnabled={false}
            connectData={undefined}
            onToggle={() => {}}
            comingSoon
          />
        )}
      </div>

      <GoogleAdsIntegrationModal
        isOpen={isOpenGoogleAdsIntegrationModal}
        onClose={() => setIsOpenGoogleAdsIntegrationModal(false)}
        connected={hasSelectedAccount}
        accountName={selectedGoogleAdsAccount?.name}
      />

      {/* Auto-opens right after Google OAuth completes (connected, accounts synced, none selected)
          — the same picker the user-menu's "Ad Accounts → Google" item opens. Closing without
          picking is allowed; a hard refresh re-opens it so a connected-but-unusable card never
          persists. #5937, mirrors the Meta picker below. */}
      <GoogleAdAccountSelectionModal isOpen={isOpenGooglePicker} onClose={() => setIsOpenGooglePicker(false)} />

      <HubSpotIntegrationModal
        isOpen={isOpenHubSpotModal}
        onClose={handleCloseHubSpotModal}
        integrationStatus={hubspotStatus || null}
      />

      <MetaIntegrationModal
        isOpen={isOpenMetaModal}
        onClose={handleCloseMetaModal}
        integrationStatus={metaStatus || null}
      />

      {/*
        Auto-opens immediately after Meta OAuth completes (no selected ad account yet) — the same
        modal the user-menu's "Ad Accounts → Meta" item opens. Closing without picking is allowed;
        a hard refresh re-opens it so a connected-but-unusable card never persists.
      */}
      <MetaAdAccountSelectionModal isOpen={isOpenMetaPicker} onClose={handleCloseMetaPicker} />
    </div>
  );
};
